import { useContext, useRef, useState, useMemo, useCallback } from 'react'
import { FiHome } from 'react-icons/fi'

import { useQueryClient } from '@tanstack/react-query'
import { Form } from '@unform/web'
import { t } from 'i18next'

import { FeedbackContext } from '~/components/feedback'
import { Button } from '~/components/Buttons'
import ToggleInput from '~/components/Toggle'
import { Badge } from '~/components/UI/Badge'
import { Checkbox, InputMoney } from '~/components/unform'
import { formatMoneyV2, isAdvertiser, convertInputMoney } from '~/helpers'

import { patchAdvertiserSettingsService } from '../../services/advertiser-publisher'

import * as S from './styles'

type WalletSettingsProps = {
  publisherConfig?: {
    auto_balance_transfer?: 'enabled' | 'disabled'
    daily_transfer_limit?: string
    monthly_transfer_limit?: string
  }
  publisherName?: string
  publisherId: string
  onClose: () => void
}

const WalletSettings = ({
  publisherConfig = {},
  publisherName = '',
  publisherId,
  onClose
}: WalletSettingsProps) => {
  const formRef = useRef(null)
  const queryClient = useQueryClient()
  const { toast } = useContext(FeedbackContext)

  const accountInfo = useMemo(() => {
    const label = isAdvertiser() ? 'Publisher' : t('rm:Advertiser')
    return { label }
  }, [])

  const initialData = useMemo(
    () => ({
      daily_limit_amount: Number(publisherConfig?.daily_transfer_limit || 0),
      monthly_limit_amount: Number(
        publisherConfig?.monthly_transfer_limit || 0
      ),
      daily_limit: !!publisherConfig?.daily_transfer_limit,
      monthly_limit: !!publisherConfig?.monthly_transfer_limit
    }),
    [publisherConfig]
  )

  const [autoTransfer, setAutoTransfer] = useState(
    publisherConfig?.auto_balance_transfer === 'enabled'
  )
  const [dailyLimitChecked, setDailyLimitChecked] = useState(
    !!publisherConfig?.daily_transfer_limit
  )
  const [monthlyLimitChecked, setMonthlyLimitChecked] = useState(
    !!publisherConfig?.monthly_transfer_limit
  )

  const handleSubmit = useCallback(
    async (data: WalletSettingsFormData) => {
      try {
        const body = {
          auto_balance_transfer: autoTransfer ? 'enabled' : 'disabled',
          daily_transfer_limit:
            dailyLimitChecked && data.daily_limit_amount?.numAsString
              ? convertInputMoney(data.daily_limit_amount.numAsString)
              : null,
          monthly_transfer_limit:
            monthlyLimitChecked && data.monthly_limit_amount?.numAsString
              ? convertInputMoney(data.monthly_limit_amount.numAsString)
              : null
        }

        await patchAdvertiserSettingsService({ publisherId, body })
        queryClient.invalidateQueries(['publisherConfig', publisherId])
        onClose?.()

        toast.success(
          autoTransfer
            ? t('rm:marketplace.rechargeAutomatically.active.message')
            : t('rm:marketplace.rechargeAutomatically.disabled.message')
        )
      } catch (error) {
        console.error('Erro ao salvar configurações de carteira:', error)
        toast.critical(
          t('rm:marketplace.rechargeAutomatically.error.description')
        )
      }
    },
    [
      autoTransfer,
      dailyLimitChecked,
      monthlyLimitChecked,
      publisherId,
      onClose,
      queryClient
    ]
  )

  return (
    <S.WalletSettingsContainer>
      <Badge iconLeft={<FiHome />} style={{ height: '22px' }}>
        {accountInfo.label}: {publisherName}
      </Badge>

      <Form ref={formRef} onSubmit={handleSubmit} initialData={initialData}>
        <S.WalletSettingsFields>
          <S.WalletSettingsField>
            <ToggleInput
              label={t('rm:marketplace.sidepanel.toggle.label')}
              description={t('rm:marketplace.sidepanel.toggle.description')}
              name="auto_transfer"
              isChecked={autoTransfer}
              onChange={({ e }: { e: React.ChangeEvent<HTMLInputElement> }) =>
                setAutoTransfer(e.target.checked)
              }
              textLeft={true}
            />
          </S.WalletSettingsField>

          {autoTransfer && (
            <>
              <S.WalletSettingsField>
                <Checkbox
                  name="daily_limit"
                  label={t('rm:marketplace.sidepanel.daily.label')}
                  isOptional={true}
                  helperText={t('rm:marketplace.sidepanel.daily.description')}
                  onChange={e => setDailyLimitChecked(e.target.checked)}
                />
                {dailyLimitChecked && (
                  <InputMoney
                    id="daily_limit_amount"
                    name="daily_limit_amount"
                    placeholder={formatMoneyV2({ value: 0 })}
                  />
                )}
              </S.WalletSettingsField>

              <S.WalletSettingsField>
                <Checkbox
                  name="monthly_limit"
                  label={t('rm:marketplace.sidepanel.monthly.label')}
                  isOptional={true}
                  helperText={t('rm:marketplace.sidepanel.monthly.description')}
                  onChange={e => setMonthlyLimitChecked(e.target.checked)}
                />
                {monthlyLimitChecked && (
                  <InputMoney
                    id="monthly_limit_amount"
                    name="monthly_limit_amount"
                    placeholder={formatMoneyV2({ value: 0 })}
                  />
                )}
              </S.WalletSettingsField>
            </>
          )}
        </S.WalletSettingsFields>

        <S.WalletSettingsFooter>
          <Button
            template="transparentCritical"
            onClick={() => {
              formRef.current?.reset()
              onClose?.()
            }}
          >
            {t('rm:button.Cancel')}
          </Button>
          <Button type="submit" template="primary">
            {t('rm:button.Save')}
          </Button>
        </S.WalletSettingsFooter>
      </Form>
    </S.WalletSettingsContainer>
  )
}

export default WalletSettings
